/** Доска судоку.
 *
 * Три уровня подсветки различаются плотностью заливки, а не цветом:
 * строка, столбец и блок — самый мягкий, та же цифра — заметно плотнее,
 * выбранная клетка — заливка плюс рамка. Один цвет в трёх плотностях
 * читается быстрее, чем три разных цвета.
 *
 * Красный зарезервирован под ошибку и больше ни подо что — поэтому её
 * видно, не читая. Различаются два вида: конфликт (цифра повторяется в
 * группе, это видно из самой доски) и ошибка (сервер сказал, что цифра
 * не совпала с решением). Первое считает клиент, второе может прийти
 * только с сервера.
 */

import { memo } from 'react'

import { CELL_COUNT, EMPTY, colOf, findConflicts, rowOf, sees } from '../game/board'
import type { Notes } from '../game/board'

const SIZE = 9

export interface BoardProps {
  /** Условие: точка — клетка, которую заполняет игрок. */
  givens: string
  board: string
  notes: Notes
  selected?: number | null
  /** Закреплённая двойным тапом цифра: подсветка держится до снятия. */
  pinnedDigit?: number | null
  /** Клетки, о неверности которых сообщил сервер. */
  errorCells?: readonly number[]
  /** Клетка, только что открытая подсказкой: рамка на три секунды. */
  hintedCell?: number | null
  /** Клетки, участвующие в объяснении приёма. */
  highlightCells?: readonly number[]
  /** Клетка, куда игрок только что поставил неверную цифру. */
  shakeCell?: number | null
  onSelect?: (cell: number) => void
  /** На паузе и после конца партии доска не отвечает на нажатия. */
  locked?: boolean
  className?: string
}

export const Board = memo(function Board({
  givens,
  board,
  notes,
  selected = null,
  pinnedDigit = null,
  errorCells = [],
  hintedCell = null,
  highlightCells = [],
  shakeCell = null,
  onSelect,
  locked = false,
  className = '',
}: BoardProps) {
  const conflicts = findConflicts(board)
  const errors = new Set(errorCells)
  const highlighted = new Set(highlightCells)

  // Закреплённая цифра важнее выбранной клетки: её для того и закрепляют,
  // чтобы вести взглядом по столбцам, не удерживая палец на клетке.
  const activeDigit =
    pinnedDigit ??
    (selected !== null && board[selected] !== EMPTY ? Number(board[selected]) : null)

  // Пока держится результат проверки, подсветка строки и цифры снимается:
  // красное не должно спорить с синим за внимание.
  const showHighlights = errors.size === 0

  const cells = []
  for (let cell = 0; cell < CELL_COUNT; cell += 1) {
    const digit = board[cell]
    const given = givens[cell] !== EMPTY
    const isSelected = selected === cell
    const cellNotes = digit === EMPTY ? (notes[String(cell)] ?? []) : []

    const classes = ['board__cell']
    if (given) classes.push('board__cell--given')
    if (showHighlights && selected !== null && sees(selected, cell)) {
      classes.push('board__cell--peer')
    }
    if (showHighlights && !isSelected && activeDigit !== null && digit === String(activeDigit)) {
      classes.push(pinnedDigit === null ? 'board__cell--same' : 'board__cell--pinned')
    }
    if (highlighted.has(cell) || hintedCell === cell) classes.push('board__cell--hinted')
    if (errors.has(cell)) classes.push('board__cell--error')
    else if (conflicts.has(cell)) classes.push('board__cell--conflict')
    if (isSelected) classes.push('board__cell--selected')
    if (shakeCell === cell) classes.push('board__cell--shake')
    if (colOf(cell) % 3 === 2) classes.push('board__cell--block-right')
    if (rowOf(cell) % 3 === 2) classes.push('board__cell--block-bottom')
    if (colOf(cell) === SIZE - 1) classes.push('board__cell--last-col')
    if (rowOf(cell) === SIZE - 1) classes.push('board__cell--last-row')

    cells.push(
      <button
        key={cell}
        type="button"
        className={classes.join(' ')}
        onClick={onSelect ? () => onSelect(cell) : undefined}
        disabled={locked || !onSelect}
        aria-label={`R${rowOf(cell) + 1}C${colOf(cell) + 1}`}
      >
        {cellNotes.length > 0 && (
          <span className="board__notes">
            {Array.from({ length: SIZE }, (_, index) => (
              <span key={index} className="board__note">
                {cellNotes.includes(index + 1) ? index + 1 : ''}
              </span>
            ))}
          </span>
        )}
        {digit !== EMPTY && (
          <span className={given ? undefined : 'board__digit--entered'}>{digit}</span>
        )}
      </button>,
    )
  }

  return <div className={`board ${className}`.trim()}>{cells}</div>
})
